import React, { useState, useEffect, useRef } from 'react';
import { X, RotateCcw, ExternalLink, ShoppingCart, AlertTriangle, Maximize, Minimize } from 'lucide-react';
import { useLanguage } from '../core/i18n/LanguageContext';

const OciFrame = ({ url, onClose, isMobile }) => {
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    // Längerer Timeout für Iframe-Loading (manche OCI-Shops sind langsam)
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.log('Iframe loading timeout - Shop lädt sehr langsam oder ist blockiert');
        // Erstmal nur als langsam markieren, nicht als blockiert
        console.log('Versuche direkten Browser-Zugriff als Alternative...');
        setIframeBlocked(true);
        setIsLoading(false);
      }
    }, 20000); // 20 Sekunden Timeout für langsame OCI-Shops

    setLoadingTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [isLoading]);

  const handleIframeLoad = () => {
    console.log('Iframe load event triggered - content loaded successfully');
    
    // Load-Event bedeutet, dass der iframe erfolgreich geladen wurde
    // Cross-origin Zugriff ist normal und bedeutet nicht, dass er blockiert ist
    setIsLoading(false);
    setHasError(false);
    setIframeBlocked(false);
    
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }
    
    // Optional: Versuche Message-Listener für OCI-Kommunikation einzurichten
    try {
      const iframe = iframeRef.current;
      if (iframe && iframe.contentWindow) {
        // Listener für mögliche OCI-Nachrichten
        const messageListener = (event) => {
          if (event.source === iframe.contentWindow) {
            console.log('OCI Frame message received:', event.data);
            // Hier könnten OCI-spezifische Nachrichten verarbeitet werden
          }
        };
        
        window.addEventListener('message', messageListener);
        
        // Cleanup funktion für später
        iframe._messageListener = messageListener;
      }
    } catch (e) {
      console.log('Cross-origin iframe - limited access (normal behavior):', e.message);
    }
  };

  const handleIframeError = () => {
    console.error('Iframe failed to load');
    setIsLoading(false);
    setHasError(true);
    
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }
  };

  const refreshFrame = () => {
    setIsLoading(true);
    setHasError(false);
    setIframeBlocked(false);
    
    // Iframe neu laden
    if (iframeRef.current) {
      iframeRef.current.src = url;
    }
  };

  const openInNewTab = () => {
    // Für mobile Geräte: Versuche, in demselben Fenster zu öffnen (ersetzt iframe)
    if (isMobile) {
      window.location.href = url;
    } else {
      // Desktop: Maximiertes Fenster für beste Erfahrung
      const features = [
        'fullscreen=yes',
        'width=' + window.screen.availWidth,
        'height=' + window.screen.availHeight,
        'left=0',
        'top=0',
        'scrollbars=yes',
        'resizable=yes',
        'toolbar=no',
        'menubar=no',
        'status=no'
      ].join(',');
      
      window.open(url, '_blank', features);
    }
  };

  const openInSameWindow = () => {
    // Alternative: Im gleichen Fenster öffnen (für mobile Geräte optimal)
    window.location.href = url;
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-white flex flex-col"
      style={{
        width: '100vw',
        height: '100vh',
        top: 0,
        left: 0,
        position: 'fixed'
      }}
    >
      {/* Header */}
      {!isFullscreen && (
        <div className="bg-blue-600 text-white p-4 flex items-center justify-between shadow-lg flex-shrink-0">
        <div className="flex items-center">
          <ShoppingCart className="w-6 h-6 mr-2" />
          <div>
            <h2 className="text-lg font-semibold">{t('oci.title')}</h2>
            <p className="text-sm text-blue-100">
              {isMobile ? 'Mobile Ansicht' : 'In-App Browser'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {/* Refresh Button */}
          <button
            onClick={refreshFrame}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
            title={t('oci.reloadTitle')}
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          
          {/* Fullscreen Toggle Button */}
          <button
            onClick={toggleFullscreen}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
            title={isFullscreen ? t('oci.showHeaderTitle') : t('oci.fullscreenTitle')}
          >
            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
          </button>
          
          {/* External Link Button */}
          <button
            onClick={openInNewTab}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
            title={t('oci.openExternal')}
          >
            <ExternalLink className="w-5 h-5" />
          </button>
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 hover:bg-blue-700 rounded-lg transition-colors"
            title={t('oci.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        </div>
      )}

      {/* Loading Indicator */}
      {isLoading && !iframeBlocked && (
        <div className={`absolute inset-0 ${isFullscreen ? 'top-0' : 'top-16'} bg-gray-50 flex items-center justify-center z-10`}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              {t('oci.loading')}
            </h3>
          </div>
        </div>
      )}

      {/* Iframe Blocked State - Automatisch im neuen Tab öffnen */}
      {iframeBlocked && (
        <div className={`absolute inset-0 ${isFullscreen ? 'top-0' : 'top-16'} bg-orange-50 flex items-center justify-center z-10`}>
          <div className="text-center max-w-md mx-auto px-4">
            <div className="text-orange-600 mb-4">
              <ExternalLink className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-orange-800 mb-2">
              {t('oci.blocked')}
            </h3>
            <p className="text-orange-600 mb-6">
              {t('oci.blockedDesc')}
            </p>
            <div className="space-y-3">
              <button
                onClick={openInNewTab}
                className="w-full bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
              >
                <ExternalLink className="w-4 h-4 inline mr-2" />
                {t('oci.openExternal')}
              </button>
              <button
                onClick={onClose}
                className="w-full text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('oci.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {hasError && !iframeBlocked && (
        <div className={`absolute inset-0 ${isFullscreen ? 'top-0' : 'top-16'} bg-red-50 flex items-center justify-center z-10`}>
          <div className="text-center max-w-md mx-auto px-4">
            <div className="text-red-600 mb-4">
              <X className="w-16 h-16 mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">
              {t('oci.error')}
            </h3>
            <div className="space-y-3">
              <button
                onClick={refreshFrame}
                className="w-full bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                <RotateCcw className="w-4 h-4 inline mr-2" />
                Erneut versuchen
              </button>
              <button
                onClick={openInNewTab}
                className="w-full border border-red-600 text-red-600 px-4 py-2 rounded-lg hover:bg-red-50 transition-colors"
              >
                <ExternalLink className="w-4 h-4 inline mr-2" />
                {t('oci.openExternal')}
              </button>
              <button
                onClick={onClose}
                className="w-full text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                {t('oci.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* iframe Container - nutzt flexbox für echte Vollbild-Darstellung */}
      {!iframeBlocked && !hasError && (
        <div className="flex-1 bg-white overflow-hidden relative">
          <iframe
            ref={iframeRef}
            id="oci-frame"
            src={url}
            className="w-full h-full border-0"
            title={t('oci.frameTitle')}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            sandbox="allow-same-origin allow-scripts allow-forms allow-top-navigation allow-top-navigation-by-user-activation"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="geolocation; microphone; camera"
          />
          
          {/* Vollbild-Steuerung schwebend */}
          {isFullscreen && (
            <div className="absolute top-4 right-4 flex space-x-2 z-20">
              <button
                onClick={toggleFullscreen}
                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg shadow-lg transition-colors"
                title={t('oci.showHeaderTitle')}
              >
                <Minimize className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg shadow-lg transition-colors"
                title={t('oci.closeTitle')}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mobile-specific instructions */}
      {isMobile && isLoading && (
        <div className="absolute bottom-4 left-4 right-4 bg-blue-900 bg-opacity-90 text-white p-4 rounded-lg z-20">
          <h4 className="font-semibold mb-2">{t('oci.mobileHint')}</h4>
        </div>
      )}
    </div>
  );
};

export default OciFrame; 